import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {EventEmitter, Injectable} from '@angular/core';
import {DynamicFormConfig, DynamicFormReadyEvent, FilterSelectFieldConfig, SelectItem} from '@mydocma/dynamic-form';
import {DocumentType} from '@docma-mm-core/document-type/document-type';
import {AttachmentData} from '@docma-mm-core/attachment/attachment';
import {MmProjectLoaderService} from '../../mm-project/mm-project-loader.service';
import {BehaviorSubject, firstValueFrom, map, Observable} from 'rxjs';
import {filter} from 'rxjs/operators';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {addAttachmentFormConfig, commentConfig, descriptionConfig, typeConfig} from './mm-add-attachments-form.config';

@UntilDestroy()
@Injectable()
export class MmAddAttachmentsFormService {



  constructor(private readonly projectLoaderService: MmProjectLoaderService) {
    this.formConfig$.next(addAttachmentFormConfig);
  }

  get description() {
    return this._description;
  }
  get comment() {
    return this._comment;
  }
  get documentType() {
    return this._documentType;
  }

  onFormReady = new EventEmitter();
  formConfig$ = new BehaviorSubject<DynamicFormConfig>(null);

  public formReadyEvent: DynamicFormReadyEvent;
  private documentTypesLoaded: DocumentType[] = [];

  attachment: AttachmentData;

  documentTypes$: Observable<SelectItem[]> = this.projectLoaderService.documentTypes.pipe(
    filter(m => m?.loadState?.status === LoadStatusEnum.Loaded),
    map(m => m.data.map(c => ({ id: c.Id, text: c.Name1 + (c.Name2 ? ' - ' + c.Name2 : ''), data: c }))),
    untilDestroyed(this));

  isInitialized = false;
  _description: string;
  _comment: string;
  _documentType: DocumentType;

  onReady(event: DynamicFormReadyEvent) {
    this.formReadyEvent = event;
    this.initialize();
    this.onFormReady.emit();
  }


  async initialize() {
    const typeFieldConfig: FilterSelectFieldConfig = {...typeConfig};
    typeFieldConfig.source = await firstValueFrom(this.documentTypes$);
    this.formReadyEvent.dynamicFormService.updateFieldConfig(typeFieldConfig);

    this.formReadyEvent.dynamicFormEventsService.onFieldValueChanged
      .pipe(untilDestroyed(this))
      .subscribe((param: any) => {
        if (this.isInitialized) {
          if (param.config.name === descriptionConfig.name) {
            this._description = param.value;
          } else if (param.config.name === commentConfig.name) {
            this._comment = param.value;
          } else if (param.config.name === typeConfig.name) {
          this._documentType = param.value;
          }
        }
      });

    this.isInitialized = true;
  }

  async loadAttachment(attachment: AttachmentData) {
    this.attachment = attachment ?? new AttachmentData();
    // CURRENTLY ONLY NEW ATTACHMENTS IMPLEMENTED!!
  }

  getChanges(files: File[] = []) {
    // Always create a new AttachmentData object for latest form values
    const attachment = new AttachmentData();

    // If no files, return a blank attachment object
    if (files.length === 0) {
      attachment.Description = null;
      attachment.Comment = null;
      attachment.DocumentType = null;
      attachment.FileName = null;
      attachment.OriginalFileName = null;
      return attachment;
    }

    // For single file, always get latest form values
    if (this.formReadyEvent?.dynamicFormService) {
      const descValue = this.formReadyEvent.dynamicFormService.getFieldValue(descriptionConfig.name);
      const commentValue = this.formReadyEvent.dynamicFormService.getFieldValue(commentConfig.name);
      const typeValue = this.formReadyEvent.dynamicFormService.getFieldValue(typeConfig.name);
      attachment.Description = descValue;
      attachment.Comment = commentValue;

      if (typeValue) {
        const typeFieldConfig = this.formReadyEvent.dynamicFormService.getFieldConfig(typeConfig.name) as FilterSelectFieldConfig;
        const documentType = typeFieldConfig.source.find(x => x.id === typeValue).data as DocumentType;
        attachment.DocumentType = documentType;
      } else {
        attachment.DocumentType = null;
      }
    }
    return attachment;
  }

  discard() {
    this._description = undefined;
    this._comment = undefined;
    this._documentType = undefined;
  }
}
